import { consola } from 'consola'
import { ref, computed } from 'vue'
import { Browser } from '@capacitor/browser'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useModalStore } from '@shared/model'
import { isNativePlatform, shouldUseCapacitorBrowser } from '@shared/lib'
import { getClientFingerprint } from '@shared/lib/auth'
import apiAuth, { useSessionManager } from '~/entities/auth'
import { useLogoutHandler } from '~/features/auth/model'
import { useAuthAnalytics } from '~/features/auth/model/useAuthAnalytics'

const logger = consola.withTag('MaxAuth')

const MAX_POLL_DURATION = 5 * 60 * 1000 // 5 минут
const POLL_INTERVAL_MS = 2000 // 2 секунды

/**
 * Быстрая авторизация через MAX-бота на сайте (Сценарий A).
 *
 * Поток (аналогичен Telegram-входу):
 *   1. POST /auth/max/generate-token → { maxLink, sessionId }
 *   2. Открываем maxLink (deep-link в бота): Capacitor Browser на мобильных, иначе новая вкладка.
 *   3. Поллим GET /auth/max/status/:sessionId каждые 2 сек (максимум 5 минут).
 *   4. status: verified (или пришли accessToken+user) → сохраняем токены, грузим профиль, закрываем модалку.
 *
 * Кнопку показываем только если у заведения настроен активный бот MAX (`max_auth_enabled`).
 */
export const useMaxAuth = () => {
  const storeInstitution = useInstitutionStore()
  const sessionManager = useSessionManager()
  const userProfileStore = useUserProfileStore()
  const logoutHandler = useLogoutHandler()
  const authAnalytics = useAuthAnalytics()
  const modalStore = useModalStore()

  const isLoading = ref(false)
  const errorMessage = ref<string | null>(null)
  const maxSessionId = ref<string | null>(null)
  const maxLink = ref<string | null>(null)
  const showMaxLinkModal = ref(false)

  let pollInterval: ReturnType<typeof setInterval> | null = null
  let pollStartTime: number | null = null

  // Доступность входа через MAX для текущего заведения.
  const isMaxAuthEnabled = computed(() => storeInstitution.institution?.max_auth_enabled === true)

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
      pollStartTime = null
      logger.debug('Опрос статуса MAX остановлен')
    }
  }

  const openMaxLink = async (url: string): Promise<boolean> => {
    logger.debug('Открытие MAX-ссылки:', url)

    if (shouldUseCapacitorBrowser()) {
      try {
        await Browser.open({ url, presentationStyle: 'popover', toolbarColor: '#ffffff' })
        return true
      } catch (error) {
        logger.warn('Ошибка открытия Capacitor Browser:', error)
        const popup = window.open(url, '_blank')
        return !!popup && !popup.closed
      }
    }

    const popup = window.open(url, '_blank')
    if (popup && !popup.closed) return true
    logger.warn('Popup заблокирован браузером')
    return false
  }

  const handleStatusChange = async (statusData: any) => {
    logger.debug('Статус MAX-авторизации:', statusData?.status)

    const isVerified =
      statusData?.status === 'verified' ||
      statusData?.status === 'used' ||
      (statusData?.accessToken && statusData?.user)
    const isExpired = statusData?.status === 'expired' || statusData?.status === 'failed'

    if (isVerified) {
      logger.success('MAX-авторизация успешна')
      authAnalytics.trackCallback('max', true)
      stopPolling()

      if (isNativePlatform()) {
        try {
          await Browser.close()
        } catch {
          /* ignore */
        }
      }

      await sessionManager.setSession(
        statusData.accessToken,
        statusData.sessionId || maxSessionId.value!,
        statusData.refreshToken,
      )
      logoutHandler.handleSuccessfulLogin()
      await userProfileStore.fetchUser()

      if (process.client) {
        try {
          const { useApplyReferralToken } = await import('~/features/profile/@x/auth')
          await useApplyReferralToken()
        } catch (error) {
          logger.warn('Реферальный токен не применён (не критично):', error)
        }
      }

      if (process.client) {
        try {
          const pushModule = await import('~/features/notifications/@x/auth')
          await pushModule.usePushNotificationSettings().initPushNotificationsAfterAuth()
        } catch (error) {
          logger.warn('Push notifications не инициализированы (не критично):', error)
        }
      }

      modalStore.close()
      isLoading.value = false
      maxSessionId.value = null
      showMaxLinkModal.value = false
    } else if (isExpired) {
      authAnalytics.trackCallback('max', false, statusData?.status || 'expired')
      logger.error('Токен MAX-авторизации истёк')
      errorMessage.value = statusData?.message || 'Ссылка устарела, попробуйте снова.'
      isLoading.value = false
      maxSessionId.value = null
      stopPolling()
    }
  }

  const startPolling = (sessionId: string) => {
    logger.debug('Начат опрос статуса MAX (каждые 2 сек, максимум 5 минут)')
    pollStartTime = Date.now()

    const checkStatus = async () => {
      if (pollStartTime && Date.now() - pollStartTime > MAX_POLL_DURATION) {
        logger.error('Превышено время ожидания MAX-авторизации')
        errorMessage.value = 'Время авторизации истекло. Попробуйте ещё раз.'
        isLoading.value = false
        stopPolling()
        return
      }

      try {
        const response = await apiAuth.checkMaxStatus(sessionId)
        await handleStatusChange(response.data)
      } catch (error: any) {
        logger.error('Ошибка проверки статуса MAX:', error)
        errorMessage.value =
          error?.status === 410
            ? 'Ссылка устарела, попробуйте снова.'
            : error?.data?.message || 'Ошибка авторизации через MAX'
        isLoading.value = false
        stopPolling()
      }
    }

    checkStatus()
    pollInterval = setInterval(checkStatus, POLL_INTERVAL_MS)
  }

  /** Открывает MAX-ссылку из модалки Safari-фолбэка (кнопка в UI). */
  const openMaxLinkManually = async () => {
    if (!maxLink.value) return
    const opened = await openMaxLink(maxLink.value)
    if (opened) showMaxLinkModal.value = false
  }

  const closeMaxLinkModal = () => {
    showMaxLinkModal.value = false
    if (pollInterval) {
      stopPolling()
      isLoading.value = false
      maxSessionId.value = null
      maxLink.value = null
    }
  }

  /** Инициирует вход через MAX-бота. */
  const initMaxAuth = async () => {
    if (typeof window === 'undefined') return

    if (!isMaxAuthEnabled.value) {
      logger.warn('MAX-авторизация недоступна для этого заведения')
      errorMessage.value = 'Вход через MAX недоступен'
      return
    }

    try {
      isLoading.value = true
      errorMessage.value = null

      const response = await apiAuth.generateMaxToken({
        institutionId: String(storeInstitution.institution?.id),
        clientFingerprint: await getClientFingerprint(),
      })

      maxSessionId.value = response.data.sessionId
      maxLink.value = response.data.maxLink

      startPolling(response.data.sessionId)

      const opened = await openMaxLink(response.data.maxLink)
      if (opened) {
        // Ушли в мессенджер: дальше исход принесёт только поллинг.
        authAnalytics.trackRedirectStart('max')
      } else {
        logger.debug('Не удалось открыть MAX автоматически — показываем модалку')
        showMaxLinkModal.value = true
        isLoading.value = false
      }
    } catch (error: any) {
      logger.error('Ошибка инициализации MAX-авторизации:', error)
      errorMessage.value = error?.data?.message || 'Произошла ошибка при авторизации через MAX'
      isLoading.value = false
    }
  }

  const cleanup = () => {
    stopPolling()
    isLoading.value = false
    errorMessage.value = null
    maxSessionId.value = null
    maxLink.value = null
    showMaxLinkModal.value = false
  }

  return {
    initMaxAuth,
    openMaxLinkManually,
    closeMaxLinkModal,
    cleanup,
    isLoading,
    errorMessage,
    isMaxAuthEnabled,
    showMaxLinkModal,
    maxLink,
  }
}
